<template>
  <div class="ai-assistant">
    <!-- 悬浮球 -->
    <div class="assistant-fab" @click="toggle" :class="{ open: open }">
      <span class="fab-icon" v-if="!open"><img :src="avatarUrl" alt="AI" /></span>
      <span class="fab-icon" v-else>✕</span>
      <span class="fab-badge" v-if="unread && !open">{{ unread }}</span>
    </div>

    <!-- 对话窗 -->
    <transition name="slide-up">
      <div class="assistant-panel" v-if="open">
        <div class="panel-head">
          <div class="head-avatar">
            <img :src="avatarUrl" alt="AI" />
          </div>
          <div class="head-info">
            <div class="head-name">{{ current }}<span class="nick-current"> · {{ title }}</span></div>
            <div class="head-status" :class="{ online: ollamaOk }">
              <span class="dot"></span>{{ ollamaOk ? '在线，随时待命' : '模型连接中…' }}
            </div>
          </div>
          <div class="head-actions">
            <button class="head-setting" @click="openSettings" title="消息通道设置（微信/飞书/钉钉）">⚙️</button>
            <button class="head-clear" @click="clearChat" title="清空对话">🗑️</button>
            <button class="head-close" @click="closePanel" title="关闭">✕</button>
          </div>
        </div>

        <!-- 消息区 -->
        <div class="msg-area" ref="msgArea">
          <div v-if="messages.length === 0" class="welcome">
            <div class="welcome-icon">👋</div>
            <p>我是{{ current }}，你的{{ title }}</p>
            <p class="welcome-sub">客源引擎的小源：查数据、找客户、看任务，说人话就行</p>
            <div class="suggest">
              <div class="suggest-title">猜你想问</div>
              <button v-for="s in suggestions" :key="s.text" class="suggest-btn" @click="quickAsk(s.text)">
                <span class="suggest-ico">{{ s.icon }}</span>
                <span class="suggest-txt">{{ s.text }}</span>
              </button>
            </div>
          </div>

          <div v-for="(m, i) in messages" :key="i" class="msg" :class="m.role">
            <div class="msg-avatar" v-if="m.role === 'assistant'"><img :src="avatarUrl" alt="AI" /></div>
            <div class="bubble user-bubble" v-if="m.role === 'user'">
              <div class="user-imgs" v-if="m.images && m.images.length">
                <img v-for="(u, j) in m.images" :key="j" :src="u" class="user-thumb" alt=""/>
              </div>
              <div class="user-text" v-if="userText(m)">{{ userText(m) }}</div>
            </div>
            <div class="bubble assistant-bubble" v-else>
              <div class="assistant-text" v-html="formatReply(m.content)"></div>
            </div>
          </div>

          <div v-if="recording || sttBusy" class="msg assistant">
            <div class="msg-avatar"><img :src="avatarUrl" alt="AI" /></div>
            <div class="bubble assistant-bubble listening">
              <span class="l-dot"></span><span class="l-dot"></span><span class="l-dot"></span>
            </div>
            <span class="listening-label">{{ recording ? '正在倾听…' : '正在识别…' }}</span>
          </div>

          <div v-if="loading" class="msg assistant">
            <div class="msg-avatar"><img :src="avatarUrl" alt="AI" /></div>
            <div class="bubble assistant-bubble typing">
              <span class="typing-dot"></span><span class="typing-dot"></span><span class="typing-dot"></span>
            </div>
          </div>
        </div>

        <!-- 设置：消息通道（微信/飞书/钉钉）内测预留入口 -->
        <div class="settings-mask" v-if="showSettings" @click.self="showSettings = false">
          <div class="settings-panel">
            <div class="settings-head">
              <span>⚙️ 消息通道设置</span>
              <button @click="showSettings = false">✕</button>
            </div>
            <p class="settings-tip">把线索提醒、爆款推送、销助问答推到聊天软件里，商家在手机上随手就能问。<b>微信已打通</b>（填 pushplus 密钥即可用，支持双向对话）；飞书/钉钉目前是意向登记，开通后由陈哥配置。</p>
            <div class="ch-item" v-for="c in channelList" :key="c.key">
              <div class="ch-left">
                <span class="ch-ico">{{ c.icon }}</span>
                <div class="ch-body">
                  <div class="ch-name">{{ c.name }}</div>
                  <div class="ch-desc">{{ c.desc }}</div>
                  <div class="ch-status">{{ c.key === 'wechat' ? (wxStatusLine || c.status) : c.status }}</div>
                </div>
              </div>
              <div class="ch-right">
                <button class="ch-cfg" v-if="c.key === 'wechat'" @click="openWxConfig">配置</button>
                <label class="ch-switch">
                  <input type="checkbox" :checked="c.key === 'wechat' ? wxForm.enabled : (channelState[c.key] || false)" @change="c.key === 'wechat' ? toggleWx($event) : toggleChannel(c.key, $event)"/>
                  <span class="ch-track"></span>
                </label>
              </div>
            </div>
            <div class="settings-foot">
              <span class="ch-tip" v-if="chSaving">保存中…</span>
              <span class="ch-tip ok" v-else-if="chSaved">已记录 ✓</span>
              <button @click="showSettings = false">完成</button>
            </div>
          </div>
        </div>

        <!-- 微信 ClawBot 配置面板（pushplus 中转密钥 + 连通性测试） -->
        <div class="settings-mask" v-if="showWxConfig" @click.self="showWxConfig = false">
          <div class="settings-panel wx-panel">
            <div class="settings-head">
              <span>💬 微信 ClawBot 配置</span>
              <button @click="showWxConfig = false">✕</button>
            </div>
            <p class="settings-tip">在 <b>pushplus.plus</b> 注册并扫码绑定 ClawBot 后，填下面两项就能双向对话。发送免费，但要<b>实名认证</b>；开放接口（拉取消息）需在「个人中心 → 开发设置」开启、配 secretKey、并把服务器公网 IP 加入<b>安全 IP</b> 白名单。</p>
            <div class="wx-field">
              <label>用户 token <span class="wx-tail" v-if="wxStatus.token_set">已填 …{{ wxStatus.token_tail }}</span></label>
              <input v-model="wxForm.token" type="password" placeholder="个人中心 → 开发设置 → 用户 token" spellcheck="false"/>
            </div>
            <div class="wx-field">
              <label>secretKey <span class="wx-tail" v-if="wxStatus.secret_set">已填 …{{ wxStatus.secret_tail }}</span></label>
              <input v-model="wxForm.secret_key" type="password" placeholder="开发设置 → secretKey（≥32 位随机串）" spellcheck="false"/>
            </div>
            <div class="wx-row">
              <div class="wx-field half">
                <label>拉取间隔（秒）</label>
                <input v-model.number="wxForm.poll_interval" type="number" min="10" max="600"/>
              </div>
              <div class="wx-field half">
                <label>回调 secret</label>
                <input v-model="wxForm.webhook_secret" type="text" placeholder="可留空；或用回调地址接收" spellcheck="false"/>
              </div>
            </div>
            <div class="wx-ops">
              <button @click="saveWxConfig" :disabled="wxBusy !== ''">保存</button>
              <button class="ghost" @click="testConnect" :disabled="wxBusy !== ''">{{ wxBusy === 'connect' ? '检测中…' : '测试连接' }}</button>
              <button class="ghost" @click="testSend" :disabled="wxBusy !== ''">{{ wxBusy === 'send' ? '发送中…' : '发测试消息' }}</button>
              <button class="ghost" @click="pollNow" :disabled="wxBusy !== ''">{{ wxBusy === 'poll' ? '处理中…' : '立即拉取' }}</button>
            </div>
            <div class="wx-result" v-if="wxMsg">
              <span class="wx-dot" :class="wxMsgKind"></span>
              <span>{{ wxMsg }}</span>
            </div>
            <div class="wx-info" v-if="wxStatus.bind_info">
              <b>ClawBot 绑定</b>：{{ wxStatus.bind_info.createTime || '已绑定' }}　对话令牌：{{ wxStatus.bind_info.haveContextToken ? '有' : '无（需先在微信里发一条消息给 ClawBot 激活）' }}
            </div>
            <div class="wx-info">
              <b>通道状态</b>：已发送 {{ wxStatus.send_total || 0 }} 条　已接收 {{ wxStatus.received_total || 0 }} 条<br>
              上次拉取：{{ wxStatus.last_poll_at || '未拉取' }}　{{ wxStatus.last_poll_result || '' }}<br>
              <span v-if="wxStatus.last_error">⚠️ {{ wxStatus.last_error }}</span>
            </div>
            <div class="wx-note">
              · 只能私聊，不能群发（获客群发仍走短信）<br>
              · 官方限制：每日 200 条请求、1 分钟 5 次；每 10 条需主动发一条消息维持通道<br>
              · 商家给 ClawBot 发消息后，销助按拉取间隔自动回答，回复也会记到网页端对话历史
            </div>
          </div>
        </div>

        <!-- 待上传图片预览：放在输入行上方单独一行，不挤占输入框宽度 -->
        <div class="img-previews" v-if="pendingImages.length || uploading">
          <div class="img-prev" v-for="(im, j) in pendingImages" :key="j">
            <img :src="im.url" alt=""/>
            <span class="img-x" @click="removeImage(j)">✕</span>
          </div>
          <div class="img-prev loading-prev" v-if="uploading">上传中…</div>
        </div>

        <!-- 输入区（豆包式：左侧「＋」上传，右侧只留 语音切换 + 发送） -->
        <div class="input-area">
          <input
            type="file"
            ref="fileInput"
            accept="image/*"
            class="file-hidden"
            @change="onPickImage"
          />

          <!-- 语音模式：按住说话大按钮 + 右侧 ⌨️ 切回文字 -->
          <template v-if="voiceMode">
            <div class="voice-hold-wrap">
              <button
                class="plus-inline"
                @click="pickImage"
                :disabled="loading || uploading"
                title="上传图片给销助看图"
              >
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
              <button
                class="voice-hold-btn"
                :class="{ speaking: recording }"
                @pointerdown="startHoldRecord"
                @pointerup="stopHoldRecord"
                @pointercancel="stopHoldRecord"
                @mousedown="startHoldRecord"
                @mouseup="stopHoldRecord"
                @touchstart.prevent="startHoldRecord"
                @touchend.prevent="stopHoldRecord"
                @touchcancel.prevent="stopHoldRecord"
                :disabled="loading"
              >
                {{ recording ? '🎙️ 松开 发送' : '🎤 按住 说话' }}
              </button>
            </div>
            <button
              class="mic-btn active"
              @click="toggleVoiceMode"
              :disabled="loading"
              title="切换到文字输入"
            >
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M6 14h.01M18 14h.01M9 14h6"/></svg>
            </button>
          </template>

          <!-- 文字模式：输入框 + 右侧 [🎤 语音切换][发送] -->
          <template v-else>
            <div class="msg-input-wrap">
              <button
                class="plus-inline"
                @click="pickImage"
                :disabled="loading || uploading"
                title="上传图片给销助看图"
              >
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
              <textarea
                v-model="input"
                ref="msgInput"
                class="msg-input"
                rows="2"
                placeholder="问我：帮我找客户 / 今天发了几个私信…"
                @input="autoResizeInput"
                @keyup.enter.exact.prevent="send"
                :disabled="loading"
              ></textarea>
            </div>
            <button
              class="mic-btn"
              @click="toggleVoiceMode"
              :disabled="loading"
              title="切换到语音输入"
            >
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            </button>
            <button
              class="send-btn"
              @click="send"
              :class="{ on: input.trim() }"
              :disabled="loading || !input.trim()"
              title="发送"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </template>
        </div>
        <div class="voice-hint" v-if="recording">🎙️ 正在录音，松开后自动识别发送</div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'AIAssistant',
  data() {
    return {
      open: false,
      loading: false,
      sttBusy: false,
      ollamaOk: true,
      unread: 0,
      input: '',
      messages: [],
      pendingImages: [],   // 待发图片 [{url, path, name}]，发送时把 path 注入消息文本供 vision_analyze 使用
      uploading: false,    // 图片上传中
      title: '智能销助',
      current: 'AI小源',
      short: '小源',
      avatar: 'assistant-5',
      avatarUrl: '/avatars/assistant-5.png',
      // 快捷案例（豆包式"猜你想问"卡片）：内容直接植入软件核心功能，点开即学会用
      suggestions: [
        { icon: '👤', text: '帮我找 50 个客户' },
        { icon: '🎬', text: '文生视频怎么用？帮我写一条描述词' },
        { icon: '🧾', text: '5件套是什么？帮我生成一份' },
        { icon: '🔥', text: '爆款复刻怎么操作？' }
      ],
      // 语音
      voiceMode: false,       // 豆包式：是否语音输入模式
      recording: false,
      mediaRecorder: null,
      audioChunks: [],
      audioStream: null,
      ttsEnabled: true,
      holdTimer: null,
      audioCtx: null,
      // 消息通道设置（销助右上角 ⚙️ 入口，内测预留）
      showSettings: false,
      channelState: {},    // { wechat: bool, feishu: bool, dingtalk: bool }
      channelList: [],     // 通道元信息 [{key, icon, name, desc, status}]
      chSaving: false,
      chSaved: false,
      // 微信 ClawBot 通道（pushplus 中转，真实双向对话）
      showWxConfig: false,
      wxForm: { token: '', secret_key: '', poll_interval: 60, user_id: 1, enabled: false, webhook_secret: '' },
      wxStatus: {},
      wxBusy: '',          // '' | 'save' | 'connect' | 'send' | 'poll'
      wxMsg: '',
      wxMsgKind: ''       // ok | err | info
    }
  },
  computed: {
    // 微信卡片下的状态摘要行（替代静态 status 文案）
    wxStatusLine() {
      const s = this.wxStatus || {}
      if (!s.token_set) return '未配置密钥（点右侧「配置」）'
      if (s.enabled) {
        const r = s.last_poll_result ? '，' + s.last_poll_result : ''
        return '已启用　接收 ' + (s.received_total || 0) + ' 条　发送 ' + (s.send_total || 0) + ' 条' + r
      }
      return '已填密钥，未启用自动回复（拉取间隔 ' + (s.poll_interval || 60) + ' 秒）'
    }
  },
  mounted() {
    // 全局唤起：其它页面（如 AI 视频页「让 AI 帮写描述」）可 dispatch 事件让销助自动回答
    window.addEventListener('ai-assistant-ask', this.onExternalAsk)
    this.loadChannels()
  },
  beforeUnmount() {
    window.removeEventListener('ai-assistant-ask', this.onExternalAsk)
  },
  methods: {
    onExternalAsk(ev) {
      const text = (ev && ev.detail && ev.detail.text) || ''
      if (!text) return
      this.voiceMode = false          // 切回文字模式
      if (this.recording) this.stopHoldRecord && this.stopHoldRecord()
      // 直接打开面板（不加载历史，避免异步覆盖即将注入的对话），并确保输入框可见可输入
      this.openPanel(false)
      this.$nextTick(() => {
        this.input = text
        this.send()
      })
    },
    // ---- 消息通道设置（微信/飞书/钉钉，内测预留入口） ----
    openSettings() {
      this.chSaved = false
      this.showSettings = true
      this.loadChannels()
    },
    async loadChannels() {
      try {
        const r = await api.get('/assistant/channel-settings')
        const ch = (r && r.channels) || {}
        this.channelState = {}
        this.channelList = []
        for (const k in ch) {
          if (!ch[k]) continue
          this.channelState[k] = !!ch[k].enabled
          this.channelList.push({
            key: k, icon: ch[k].icon, name: ch[k].name,
            desc: ch[k].desc, status: ch[k].status
          })
        }
      } catch (e) {
        // 后端未重启或接口不存在时静默：面板留空，不打断用户
      }
    },
    // ---- 微信 ClawBot（pushplus 中转，真实双向对话） ----
    async openWxConfig() {
      this.showWxConfig = true
      this.wxMsg = ''
      this.wxMsgKind = ''
      try {
        const r = await api.get('/wechat/config')
        if (r) {
          this.wxStatus = r
          this.wxForm = {
            token: '',                       // 密钥不回明文，只在用户输入时提交
            secret_key: '',
            poll_interval: r.poll_interval || 60,
            user_id: r.user_id || 1,
            enabled: !!r.enabled,
            webhook_secret: ''
          }
        }
      } catch (e) {
        this.wxMsgKind = 'err'
        this.wxMsg = '读取配置失败：' + (e && e.message ? e.message : '后端未重启？')
      }
    },
    async saveWxConfig() {
      this.wxBusy = 'save'
      this.wxMsgKind = ''
      try {
        const r = await api.post('/wechat/config', { ...this.wxForm })
        if (r && r.ok) {
          this.wxStatus = r.public || {}
          this.wxForm.enabled = !!r.public.enabled
          this.wxMsgKind = 'ok'
          this.wxMsg = '已保存。' + (this.wxForm.enabled ? '自动回复已开启。' : '自动回复未开启。')
          this.loadChannels()
        }
      } catch (e) {
        this.wxMsgKind = 'err'
        this.wxMsg = '保存失败：' + (e && e.message ? e.message : '')
      } finally {
        this.wxBusy = ''
      }
    },
    async toggleWx(ev) {
      const val = ev.target.checked
      this.wxForm.enabled = val
      try {
        const r = await api.post('/wechat/config', { ...this.wxForm })
        if (r && r.ok) {
          this.wxStatus = r.public || {}
          this.wxForm.enabled = !!r.public.enabled
        } else {
          ev.target.checked = !val
        }
      } catch (e) {
        ev.target.checked = !val   // 失败回滚，避免假成功
      }
    },
    async testConnect() {
      this.wxBusy = 'connect'
      this.wxMsgKind = ''
      try {
        const r = await api.post('/wechat/test-connect', null, { timeout: 60000 })
        if (r && r.ok) {
          this.wxStatus.bind_info = r.data || {}
          this.wxMsgKind = 'ok'
          this.wxMsg = '连接成功：token / secretKey / 安全 IP 全部有效，ClawBot 已绑定。'
        } else {
          this.wxMsgKind = 'err'
          this.wxMsg = '连接失败：' + (r && r.error ? r.error : '未知原因')
        }
      } catch (e) {
        this.wxMsgKind = 'err'
        this.wxMsg = '连接失败：' + (e && e.message ? e.message : '')
      } finally {
        this.wxBusy = ''
      }
    },
    async testSend() {
      this.wxBusy = 'send'
      this.wxMsgKind = 'info'
      this.wxMsg = '正在发送测试消息…'
      try {
        const r = await api.post('/wechat/test-send', null, { timeout: 60000 })
        const res = (r && r.result) || {}
        if (r && r.ok) {
          this.wxMsgKind = 'ok'
          this.wxMsg = '已受理（流水号 ' + String(res.short_code || '').slice(0, 8) + '…），请到 ClawBot 里看。发送是异步的，几秒内到达。'
        } else {
          this.wxMsgKind = 'err'
          this.wxMsg = '发送失败：' + (res.hint || res.msg || '请检查 token 与实名状态')
        }
      } catch (e) {
        this.wxMsgKind = 'err'
        this.wxMsg = '发送失败：' + (e && e.message ? e.message : '')
      } finally {
        this.wxBusy = ''
      }
    },
    async pollNow() {
      this.wxBusy = 'poll'
      this.wxMsgKind = 'info'
      this.wxMsg = '正在拉取微信消息并生成回复…（含销助思考，可能要十几秒）'
      try {
        const r = await api.post('/wechat/poll', null, { timeout: 180000 })
        if (!r || r.error) {
          this.wxMsgKind = 'err'
          this.wxMsg = '拉取失败：' + (r && r.error ? r.error : '未知原因')
        } else {
          this.wxMsgKind = r.handled > 0 ? 'ok' : 'info'
          this.wxMsg = '拉到 ' + (r.received || 0) + ' 条，自动回复 ' + (r.handled || 0) + ' 条，耗时 ' + (r.cost_sec || 0) + ' 秒。'
          if (r.results && r.results.length) {
            const bad = r.results.filter(x => !x.ok || x.status !== 'ok')
            if (bad.length) this.wxMsg += ' 失败：' + bad.map(x => x.error || '未知').join('；')
          }
        }
        const c = await api.get('/wechat/config')
        if (c) this.wxStatus = c
      } catch (e) {
        this.wxMsgKind = 'err'
        this.wxMsg = '拉取失败：' + (e && e.message ? e.message : '')
      } finally {
        this.wxBusy = ''
      }
    },
    async toggleChannel(key, ev) {
      const val = ev.target.checked
      this.chSaving = true
      this.chSaved = false
      this.channelState[key] = val
      try {
        const r = await api.post('/assistant/channel-settings', { channels: { ...this.channelState } })
        if (r && r.ok) {
          this.chSaved = true
          setTimeout(() => { this.chSaved = false }, 2500)
        }
      } catch (e) {
        this.channelState[key] = !val   // 保存失败回滚开关，避免假成功
      } finally {
        this.chSaving = false
      }
    },
    openPanel(withHistory = true) {
      this.open = true
      this.unread = 0
      this.unlockAudio()
      this.checkHealth()
      this.loadNickname()
      if (withHistory) this.loadHistory()
      this.$nextTick(() => this.scrollBottom())
    },
    closePanel() {
      this.open = false
    },
    toggle() {
      if (this.open) this.closePanel()
      else this.openPanel(true)
    },
    async loadHistory() {
      // 打开面板时恢复最近对话记录（自动保存）
      try {
        const res = await api.get('/assistant/history?limit=50')
        if (res && res.items && res.items.length) {
          this.messages = res.items.map(h => ({
            role: h.role,
            content: h.content,
            toolCalls: h.tool_names ? [{ name: h.tool_names.split(',')[0] }] : []
          }))
          this.$nextTick(() => this.scrollBottom())
        }
      } catch (e) { /* 历史加载失败不阻塞 */ }
    },
    async loadNickname() {
      try {
        const res = await api.get('/assistant/name')
        if (res) {
          this.current = res.current || 'AI小源'
          this.short = res.short || '小源'
          this.title = res.title || '智能销助'
          this.avatar = res.avatar || 'assistant-5'
          this.avatarUrl = res.avatar_url || '/avatars/assistant-5.png'
        }
      } catch (e) {
        // 默认值兜底
      }
    },
    async checkHealth() {
      try {
        const res = await api.get('/assistant/health')
        // 兼容新旧字段：新版返回 provider/status，旧版返回 ollama
        this.ollamaOk = res?.status === 'ok' || res?.ollama === 'ok'
      } catch (e) {
        this.ollamaOk = false
      }
    },
    // ============ 语音输入（豆包式：模式切换 + 按住说话） ============
    async toggleVoiceMode() {
      // 切换语音/文字模式，切换时如果有录音先停
      if (this.recording) this.stopHoldRecord()
      this.voiceMode = !this.voiceMode
      if (this.voiceMode) {
        this.input = ''   // 切语音模式清空输入框（模拟豆包）
        // 进入语音模式时预请求麦克风权限（此时用户有明确点击手势，可从容点"允许"）
        await this.ensureMicPermission()
      }
    },
    // 预请求/检查麦克风权限（缓存授权流，避免按住时才弹窗）
    async ensureMicPermission() {
      if (this.audioStream) return true   // 已有授权流
      // 麦克风只在安全上下文给权限：HTTPS 或本机 localhost / 127.0.0.1
      if (!window.isSecureContext || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.$message?.error?.('麦克风需要 HTTPS 访问：请用 https://demo.zhaokehu.vip 打开——局域网 http://192.168.0.101 这类地址浏览器不给麦克风权限')
        return false
      }
      // 检查权限状态（Permission API，Chrome 支持）
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const status = await navigator.permissions.query({ name: 'microphone' })
          if (status.state === 'denied') {
            this.$message?.error?.('麦克风权限已被拒绝，请在浏览器地址栏点🔒图标 → 网站设置 → 允许麦克风')
            return false
          }
          if (status.state === 'prompt') {
            this.$message?.info?.('请在弹窗中点「允许」使用麦克风')
          }
        }
      } catch (e) { /* Permission API 不支持则跳过 */ }
      try {
        this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        return true
      } catch (err) {
        const msg = (err && err.message) || ''
        if (msg.includes('Permission') || msg.includes('denied')) {
          this.$message?.error?.('麦克风未授权：请在浏览器地址栏点🔒图标 → 网站设置 → 允许麦克风，然后刷新页面')
        } else {
          this.$message?.error?.('无法访问麦克风：' + msg)
        }
        return false
      }
    },
    // 按住开始录音（权限已预请求，直接用缓存流）
    async startHoldRecord(e) {
      if (this.loading || this.recording) return
      if (e && e.preventDefault) e.preventDefault()
      clearTimeout(this.holdTimer)
      this._recStartAt = Date.now()
      this._recTooShort = false
      this.unlockAudio()
      // 确保有授权流（若被回收则再请求一次）
      if (!this.audioStream || this.audioStream.getAudioTracks().some(t => t.readyState !== 'live')) {
        this.stopStream()
        const ok = await this.ensureMicPermission()
        if (!ok) return
      }
      try {
        this.mediaRecorder = new MediaRecorder(this.audioStream)
        this.audioChunks = []
        this.mediaRecorder.ondataavailable = (ev) => {
          if (ev.data.size > 0) this.audioChunks.push(ev.data)
        }
        this.mediaRecorder.onstop = () => this.uploadAudio()
        this.mediaRecorder.start()
        this.recording = true
      } catch (err) {
        this.$message?.error?.('录音启动失败：' + (err.message || err))
        this.stopStream()
      }
    },
    // 松开结束录音 → 自动识别发送
    stopHoldRecord(e) {
      if (e && e.preventDefault) e.preventDefault()
      if (!this.recording) return
      const ms = Date.now() - (this._recStartAt || Date.now())
      if (ms < 400) this._recTooShort = true   // 过短丢弃，防手机端误触当成录音
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop()
      }
      // 注意：不立即释放 stream（保留权限，避免每次弹窗），仅停止录制
      this.recording = false
    },
    stopStream() {
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(t => t.stop())
        this.audioStream = null
      }
    },
    async uploadAudio() {
      if (this._recTooShort) {
        this._recTooShort = false
        this.audioChunks = []
        this.$message?.warning?.('按住时间长一点再松开（至少 0.5 秒）')
        return
      }
      if (!this.audioChunks.length) {
        this.$message?.warning?.('没有录到声音，请检查麦克风后重试')
        return
      }
      const blob = new Blob(this.audioChunks, { type: 'audio/webm' })
      this.audioChunks = []
      // 录音过短（<0.5s 约 2KB）直接提示，避免上传无效音频
      if (blob.size < 2000) {
        this.$message?.warning?.('说话太短了，请按住说话 1 秒以上')
        return
      }
      const fd = new FormData()
      fd.append('file', blob, 'voice.webm')
      this.loading = true
      this.sttBusy = true
      try {
        const res = await api.post('/assistant/stt', fd, { timeout: 60000, headers: { 'Content-Type': 'multipart/form-data' } })
        const text = (res?.text || '').trim()
        if (text) {
          this.input = text
          this.loading = false  // 先复位，否则 send() 的 loading 保护会拦截
          this.sttBusy = false
          this.send()  // 自动发送
        } else {
          this.$message?.error?.('未识别到语音，请重试')
        }
      } catch (e) {
        this.$message?.error?.('语音识别失败：' + (e.message || e))
      } finally {
        this.loading = false
        this.sttBusy = false
      }
    },
    // ============ 语音播报（TTS） ============
    sanitizeForTTS(text) {
      // 只播报纯文字：去掉 markdown 符号、代码、星号、emoji 表情等
      return (text || '')
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/`[^`]*`/g, ' ')
        .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
        .replace(/[*_~#>|]/g, ' ')
        .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}\u{2190}-\u{21FF}\u{2B50}\u{2705}]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    unlockAudio() {
      // 在用户手势时机解锁音频（手机浏览器 autoplay 策略：异步播放会被拦截）
      try {
        if (!this.audioCtx) {
          const AC = window.AudioContext || window.webkitAudioContext
          if (AC) this.audioCtx = new AC()
        }
        if (this.audioCtx && this.audioCtx.state === 'suspended') {
          this.audioCtx.resume()
        }
      } catch (e) { /* ignore */ }
    },
    async speakReply(text) {
      if (!this.ttsEnabled || !text) return
      const clean = this.sanitizeForTTS(text)
      if (!clean) return
      this.unlockAudio()
      try {
        const res = await api.post('/assistant/tts', { text: clean }, { timeout: 60000, responseType: 'blob' })
        // api 拦截器返回 res.data，blob 场景直接是 blob
        const blob = res instanceof Blob ? res : new Blob([res])
        const url = URL.createObjectURL(blob)
        // 优先 Web Audio 播放（iOS Safari/Chrome 上 HTMLAudio 会被自动播放策略拦截，Web Audio 在手势解锁后可靠）
        try {
          if (this.audioCtx && this.audioCtx.state === 'running') {
            const buf = await this.audioCtx.decodeAudioData(await blob.arrayBuffer())
            const src = this.audioCtx.createBufferSource()
            src.buffer = buf
            const gain = this.audioCtx.createGain()
            gain.gain.value = 1.0   // 最大音量
            src.connect(gain)
            gain.connect(this.audioCtx.destination)
            src.onended = () => URL.revokeObjectURL(url)
            src.start(0)
            return
          }
        } catch (e1) { /* Web Audio 失败则降级 Audio 元素 */ }
        // 降级：Audio 元素播放（含 muted 兜底）
        const audio = new Audio(url)
        audio.volume = 1.0   // 手机外放最大音量
        audio.onended = () => URL.revokeObjectURL(url)
        const p = audio.play()
        if (p && p.catch) {
          p.catch(() => {
            // 自动播放被拦截时兜底：muted 播放解锁后再出声（手机 Chrome/微信）
            audio.muted = true
            const p2 = audio.play()
            if (p2 && p2.then) {
              p2.then(() => { setTimeout(() => { audio.muted = false }, 100) }).catch(() => {})
            }
          })
        }
      } catch (e) {
        // 语音播报失败不阻塞对话
      }
    },
    quickAsk(text) {
      this.input = text
      this.send()
    },
    clearChat() {
      this.messages = []
      this.pendingImages = []
    },
    // ---- 图片上传：发给销助看图（后端 vision_analyze 使用绝对路径） ----
    pickImage() {
      if (this.loading || this.uploading) return
      const el = this.$refs.fileInput
      if (el) el.click()
    },
    // 输入框随内容自适应高度（上限 96px）
    autoResizeInput() {
      const el = this.$refs.msgInput
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight + 2, 96) + 'px'
    },
    // 手机端压缩图片：最长边压到 1600px、JPEG 0.82，把 10MB 原图压到几百 KB，避免上传超时
    compressImage(file) {
      return new Promise((resolve) => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => {
          try {
            const MAX = 1600
            const k = Math.min(1, MAX / Math.max(img.width, img.height))
            const w = Math.max(1, Math.round(img.width * k))
            const h = Math.max(1, Math.round(img.height * k))
            const cv = document.createElement('canvas')
            cv.width = w
            cv.height = h
            const cx = cv.getContext('2d')
            cx.fillStyle = '#fff'
            cx.fillRect(0, 0, w, h)
            cx.drawImage(img, 0, 0, w, h)
            cv.toBlob((b) => {
              if (b && b.size < file.size) {
                resolve(new File([b], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }))
              } else {
                resolve(file)   // 压缩没变小（比如截图）就传原图
              }
            }, 'image/jpeg', 0.82)
          } catch (err) {
            resolve(file)
          } finally {
            URL.revokeObjectURL(url)
          }
        }
        img.onerror = () => { URL.revokeObjectURL(url); resolve(file) }
        img.src = url
      })
    },
    async onPickImage(e) {
      const files = Array.from(e.target.files || []).slice(0, 4)
      e.target.value = ''
      for (const f of files) {
        if (!/^image\//.test(f.type)) continue
        if (f.size > 25 * 1024 * 1024) {
          this.messages.push({ role: 'assistant', content: '⚠️ 图片超过 25MB，请压缩后再传。' })
          continue
        }
        this.uploading = true
        try {
          const target = await this.compressImage(f)   // 手机端先压小，根治超时
          const fd = new FormData()
          fd.append('file', target)
          const res = await api.post('/assistant/upload-image', fd, { timeout: 180000 })
          if (res.ok) {
            this.pendingImages.push({ url: res.url, path: res.path, name: res.name })
          }
        } catch (err) {
          this.messages.push({ role: 'assistant', content: '⚠️ 图片上传失败：' + (err.message || err) })
        } finally {
          this.uploading = false
        }
      }
      this.scrollBottom()
    },
    removeImage(j) {
      this.pendingImages.splice(j, 1)
    },
    // 用户气泡只显示文字，隐藏注入给模型的图片路径
    userText(m) {
      return String(m.content || '').split('【附图】')[0].trim()
    },
    async send() {
      const text = this.input.trim()
      const imgs = this.pendingImages.slice()
      if ((!text && !imgs.length) || this.loading) return
      this.input = ''
      this.pendingImages = []
      this.unlockAudio()
      let content = text
      for (const im of imgs) {
        content += (content ? '\n' : '') + '【附图】图片路径：' + im.path
      }
      this.messages.push({ role: 'user', content: content, images: imgs.map(i => i.url) })
      this.loading = true
      this.scrollBottom()
      try {
        // 只传最近 10 条，保证上下文（图片路径在文本里，销助可用 vision_analyze 看图）
        const history = this.messages
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .slice(-10)
          .map(m => ({ role: m.role, content: m.content }))
        // 带 user_id：后端按商户隔离画像/线索/任务/账号（多商户千人千面）
        const res = await api.post('/assistant/chat', {
          messages: history,
          user_id: Number(localStorage.getItem('user_id')) || 0
        }, { timeout: 180000 })
        this.messages.push({
          role: 'assistant',
          content: res.reply || '…',
          toolCalls: res.tool_calls || []
        })
        this.speakReply(res.reply)  // TTS 播报
      } catch (e) {
        this.messages.push({
          role: 'assistant',
          content: '⚠️ 连接助手失败：' + (e.message || e) + '。请检查网络后重试。'
        })
      } finally {
        this.loading = false
        this.scrollBottom()
      }
    },
    formatReply(text) {
      if (!text) return ''
      // 简单格式化：换行 → <br>，加粗 **xx** → <b>，图片 ![alt](url) → <img>
      return String(text)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
        .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="qr-img"/>')
        .replace(/\n/g, '<br/>')
    },
    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.msgArea
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.ai-assistant {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* 悬浮球 */
.assistant-fab {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #22d3ee);
  box-shadow: 0 6px 24px rgba(37, 99, 235, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}
.assistant-fab:hover { transform: scale(1.08); box-shadow: 0 8px 30px rgba(34, 211, 238, 0.5); }
.assistant-fab.open { background: #1f2937; }
.fab-icon { font-size: 28px; line-height: 1; width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; }
.fab-icon img { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; display: block; }
.fab-badge {
  position: absolute; top: -4px; right: -4px;
  background: #ef4444; color: #fff; font-size: 11px;
  min-width: 20px; height: 20px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  padding: 0 5px;
}

/* 面板 */
.assistant-panel {
  position: absolute;
  right: 0;
  bottom: 72px;
  width: 400px;
  height: 700px;
  max-height: calc(100vh - 120px);
  background: #0e1526;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(148,163,184,.16);
}

.slide-up-enter-active, .slide-up-leave-active { transition: all 0.25s ease; }
.slide-up-enter-from, .slide-up-leave-to { opacity: 0; transform: translateY(16px); }

/* 头部 */
.panel-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #2563eb, #22d3ee);
  color: #fff;
}
.head-avatar {
  width: 40px; height: 40px; border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: transform .15s;
}
.head-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.head-info { flex: 1; }
.head-name { font-weight: 700; font-size: 15px; }
.head-status { font-size: 12px; opacity: 0.85; display: flex; align-items: center; gap: 5px; }
.head-status .dot { width: 7px; height: 7px; border-radius: 50%; background: #f59e0b; display: inline-block; }
.head-status.online .dot { background: #34d399; }
.head-clear {
  background: none; border: none; color: #fff; font-size: 16px;
  cursor: pointer; opacity: 0.7; transition: opacity 0.2s;
}
.head-clear:hover { opacity: 1; }
/* 右上角「⚙️ 设置」小标识：消息通道入口（内测预留） */
.head-setting {
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  font-size: 13px;
  padding: 3px 9px;
  border-radius: 10px;
  cursor: pointer;
  opacity: 0.9;
  transition: all 0.2s;
  line-height: 1.4;
}
.head-setting:hover { opacity: 1; background: rgba(255, 255, 255, 0.28); }
.head-close {
  background: rgba(255, 255, 255, 0.14); border: none; color: #fff;
  width: 22px; height: 22px; line-height: 20px; border-radius: 50%;
  cursor: pointer; font-size: 12px; opacity: 0.85; transition: opacity 0.2s;
  margin-left: 2px; text-align: center;
}
.head-close:hover { opacity: 1; background: rgba(239, 68, 68, 0.55); }
.head-actions { display: flex; align-items: center; gap: 6px; }
.nick-current { font-weight: 600; font-size: 13px; opacity: .78; }

/* 消息区 */
.msg-area {
  flex: 1;
  overflow-y: auto;
  padding: 16px 16px 32px;
  background: #f8fafc;
}
.msg-area::-webkit-scrollbar { width: 5px; }
.msg-area::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

.welcome { text-align: center; padding: 30px 10px 10px; }
.welcome-icon { font-size: 44px; margin-bottom: 10px; }
.welcome p { color: #334155; font-weight: 600; font-size: 17px; margin: 0 0 4px; }
.welcome-sub { color: #64748b !important; font-weight: 400 !important; font-size: 15px !important; }
.suggest { display: flex; flex-direction: column; gap: 8px; margin-top: 16px; padding: 0 4px; }
.suggest-title {
  font-size: 12px; color: #7c8aa5; font-weight: 600;
  padding: 0 4px; margin-bottom: 2px; letter-spacing: 0.5px;
}
.suggest-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 11px 14px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;
}
.suggest-ico { font-size: 18px; flex-shrink: 0; }
.suggest-txt { color: #e2e8f0; line-height: 1.4; }
.suggest-btn:hover {
  border-color: #2563eb;
  background: rgba(37, 99, 235, 0.16);
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
}
.suggest-btn:hover .suggest-txt { color: #93c5fd; }

.msg { display: flex; gap: 8px; margin-bottom: 14px; }
.msg.user { justify-content: flex-end; }
.msg-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #22d3ee);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; overflow: hidden;
}
.msg-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.bubble {
  max-width: 94%;
  padding: 12px 16px;
  border-radius: 14px;
  font-size: 16px;
  line-height: 1.65;
  word-break: break-word;
  background: #fff;
  color: #1f2937;
  border-bottom-left-radius: 4px;
}
.msg.user .bubble {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.assistant-bubble {
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #1f2937;
  border-bottom-left-radius: 4px;
}
.qr-img {
  max-width: 220px; width: 100%; height: auto;
  border-radius: 8px; border: 1px solid #e2e8f0;
  margin-top: 8px; display: block;
}
.tool-tag {
  display: inline-block;
  font-size: 11px; color: #7c3aed;
  background: #f3e8ff; padding: 2px 8px;
  border-radius: 8px; margin-bottom: 6px;
}
.assistant-text { white-space: normal; }

/* 打字动画 */
.typing { display: flex; gap: 4px; padding: 12px 14px; }
.typing-dot {
  width: 7px; height: 7px; border-radius: 50%; background: #94a3b8;
  animation: blink 1.2s infinite;
}
.typing-dot:nth-child(2) { animation-delay: 0.2s; }
.typing-dot:nth-child(3) { animation-delay: 0.4s; }
.listening { display: flex; gap: 4px; padding: 12px 14px; }
.l-dot {
  width: 7px; height: 7px; border-radius: 50%; background: #2563eb;
  animation: lwave 1s infinite ease-in-out;
}
.l-dot:nth-child(2) { animation-delay: 0.15s; }
.l-dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes lwave {
  0%, 100% { transform: translateY(0); opacity: 0.45; }
  50% { transform: translateY(-5px); opacity: 1; }
}
.listening-label {
  font-size: 12px; color: #64748b; margin-left: 10px; align-self: center;
  white-space: nowrap;
}
@keyframes blink { 0%, 80%, 100% { opacity: 0.3; } 40% { opacity: 1; } }

/* 输入区 */
.input-area {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: #0e1526;
  border-top: 1px solid #e5e7eb;
}
/* 豆包式：「＋」上传做进输入框内部，不额外占宽 → 文本框更宽 */
.msg-input-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}
.plus-inline {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  z-index: 3;
  flex-shrink: 0;
}
.plus-inline:hover { background: rgba(37, 99, 235, 0.12); color: #2563eb; }
.plus-inline:disabled { opacity: 0.4; cursor: not-allowed; }
/* 语音模式下「＋」浮在按住说话按钮内左侧 */
.voice-hold-wrap { position: relative; }
.mic-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
}
.mic-btn:hover { border-color: #2563eb; background: rgba(37, 99, 235, 0.18); color: #93c5fd; }
.mic-btn.active {
  background: #2563eb;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
  color: #fff;
}
.mic-btn.recording {
  background: #ef4444;
  border-color: #ef4444;
  animation: pulse 1.2s infinite;
}
@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5); }
  50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
}
/* 豆包式：按住说话 */
.voice-hold-wrap {
  flex: 1;
  display: flex;
}
.voice-hold-btn {
  flex: 1;
  height: 48px;
  touch-action: none;            /* 手机端防止手指微移触发 pointerleave 提前掐断录音 */
  user-select: none;
  -webkit-user-select: none;
  border-radius: 24px;
  border: 1px solid #2563eb;
  background: #eff6ff;
  color: #2563eb;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
}
.voice-hold-btn:hover { background: #dbeafe; }
.voice-hold-btn.speaking {
  background: #ef4444;
  border-color: #ef4444;
  color: #fff;
  animation: pulse 1s infinite;
}
/* 「＋」浮在按住说话按钮内左侧，文字让位 */
.voice-hold-btn { padding-left: 44px; }
.voice-hint {
  text-align: center;
  font-size: 12px;
  color: #ef4444;
  background: #fef2f2;
  padding: 6px;
  border-top: 1px solid #fecaca;
}
.msg-input {
  flex: 1;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px 12px 38px;   /* 左侧让位给「＋」按钮 */
  font-size: 16px;
  line-height: 1.5;
  min-height: 58px;               /* 拉高输入条，视觉更舒展 */
  max-height: 96px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  resize: none;
  font-family: inherit;
  color: #0f172a;
}
.msg-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
/* 豆包式：圆形发送按钮，有内容才点亮 */
.send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: not-allowed;
  transition: all 0.2s;
  flex-shrink: 0;
}
.send-btn.on {
  background: linear-gradient(135deg, #2563eb, #22d3ee);
  color: #fff;
  border-color: transparent;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(37, 99, 235, 0.45);
}
.send-btn.on:hover { opacity: 0.92; transform: scale(1.06); }

/* 手机端适配：面板贴近满宽、气泡放宽——文字不再被竖着压 */
@media (max-width: 767px) {
  .ai-assistant { right: 8px; bottom: 8px; }
  .assistant-fab { width: 50px; height: 50px; }
  .assistant-panel { width: calc(100vw - 16px); height: 88vh; right: 8px; bottom: 58px; }
  .bubble { max-width: 94%; padding: 11px 14px; }
  .msg { margin-bottom: 12px; }
  .msg-avatar { width: 28px; height: 28px; }
}

/* ---- 图片上传入口（发给销助看图） ---- */
.file-hidden { display: none; }
.img-previews {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 8px 12px 0;
}
.img-prev {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #f1f5f9;
  flex-shrink: 0;
}
.img-prev img { width: 100%; height: 100%; object-fit: cover; display: block; }
.img-prev.loading-prev {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #64748b;
}
.img-x {
  position: absolute;
  top: 0;
  right: 0;
  width: 16px;
  height: 16px;
  line-height: 15px;
  text-align: center;
  font-size: 11px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  border-bottom-left-radius: 8px;
}
.img-x:hover { background: rgba(0, 0, 0, 0.8); }

/* 用户消息气泡里的图片缩略图 */
.user-bubble { display: flex; flex-direction: column; gap: 6px; }
.user-imgs { display: flex; gap: 4px; flex-wrap: wrap; }
.user-thumb {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
}
@media (max-width: 767px) {
  .user-thumb { width: 60px; height: 60px; }
  .img-prev { width: 48px; height: 48px; }
}
/* ===== 设置：消息通道面板（微信/飞书/钉钉，内测预留入口） ===== */
.settings-mask {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}
.settings-panel {
  width: 100%;
  max-width: 340px;
  max-height: 80%;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}
.settings-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 10px;
}
.settings-head button {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 14px;
  cursor: pointer;
  padding: 2px 6px;
}
.settings-head button:hover { color: #ef4444; }
.settings-tip {
  font-size: 12px;
  line-height: 1.6;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 9px 11px;
  margin: 0 0 12px;
}
.ch-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px;
  margin-bottom: 9px;
}
.ch-left { flex: 1; display: flex; gap: 10px; min-width: 0; }
.ch-ico { font-size: 20px; line-height: 1.2; }
.ch-body { min-width: 0; }
.ch-name { font-size: 13px; font-weight: 600; color: #0f172a; }
.ch-desc { font-size: 11px; line-height: 1.55; color: #64748b; margin-top: 3px; }
.ch-status {
  font-size: 11px;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 1px 6px;
  display: inline-block;
  margin-top: 5px;
}
.ch-switch {
  position: relative;
  width: 40px;
  height: 22px;
  flex-shrink: 0;
  cursor: pointer;
}
.ch-switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.ch-track {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 11px;
  transition: background 0.2s;
}
.ch-track::after {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  top: 2px;
  left: 2px;
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.ch-switch input:checked + .ch-track { background: #2563eb; }
.ch-switch input:checked + .ch-track::after { left: 20px; }

/* ---- 微信卡片：右侧「配置」按钮 ---- */
.ch-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.ch-cfg {
  font-size: 12px;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 4px 11px;
  cursor: pointer;
  line-height: 1.4;
}
.ch-cfg:hover { background: #dbeafe; border-color: #93c5fd; }

/* ---- 微信 ClawBot 配置面板 ---- */
.wx-panel { width: 520px; max-width: 92vw; max-height: 88vh; }
.wx-field { margin-bottom: 10px; }
.wx-field label {
  display: block;
  font-size: 12px;
  color: #475569;
  margin-bottom: 5px;
  font-weight: 600;
}
.wx-tail { color: #16a34a; font-weight: 400; margin-left: 6px; }
.wx-field input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: #0f172a;
  background: #fff;
  outline: none;
}
.wx-field input:focus { border-color: #2563eb; }
.wx-row { display: flex; gap: 10px; }
.wx-row .half { flex: 1; min-width: 0; }
.wx-ops { display: flex; gap: 8px; flex-wrap: wrap; margin: 12px 0 10px; }
.wx-ops button {
  font-size: 13px;
  padding: 7px 14px;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  background: #2563eb;
  color: #fff;
  transition: background 0.15s;
}
.wx-ops button:hover { background: #1d4ed8; }
.wx-ops button.ghost { background: #fff; color: #2563eb; border: 1px solid #bfdbfe; }
.wx-ops button.ghost:hover { background: #eff6ff; }
.wx-ops button:disabled { opacity: 0.55; cursor: not-allowed; }
.wx-result {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 13px;
  line-height: 1.55;
  padding: 9px 11px;
  border-radius: 8px;
  margin-bottom: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
}
.wx-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #94a3b8;
  flex-shrink: 0;
  margin-top: 6px;
}
.wx-dot.ok { background: #22c55e; }
.wx-dot.err { background: #ef4444; }
.wx-dot.info { background: #3b82f6; }
.wx-info { font-size: 12px; line-height: 1.75; color: #475569; margin-bottom: 8px; }
.wx-info b { color: #0f172a; }
.wx-note { font-size: 11.5px; line-height: 1.75; color: #64748b; }

.settings-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}
.settings-foot .ch-tip.ok { color: #16a34a; }
.settings-foot button {
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 16px;
  font-size: 12px;
  cursor: pointer;
}
.settings-foot button:hover { background: #1d4ed8; }
</style>
